import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Tooltip from './Tooltip';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';

interface WaitlistPositionBadgeProps {
  position: number;
  previousPosition?: number;
  animated?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * WaitlistPositionBadge - Display waitlist position with animations
 * Shows position number with color coding and directional indicators
 *
 * Accessibility (Issue #499):
 * - Respects prefers-reduced-motion
 * - ARIA labels for screen readers
 * - Keyboard focusable with focus-visible styles
 */
function WaitlistPositionBadge({
  position,
  previousPosition,
  animated = true,
  size = 'md'
}: WaitlistPositionBadgeProps) {
  const [showAnimation, setShowAnimation] = useState(false);
  const [positionChanged, setPositionChanged] = useState<'up' | 'down' | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Detect position changes
  useEffect(() => {
    if (previousPosition && previousPosition !== position && animated && !prefersReducedMotion) {
      setPositionChanged(position < previousPosition ? 'up' : 'down');
      setShowAnimation(true);

      const timer = setTimeout(() => {
        setShowAnimation(false);
        setPositionChanged(null);
      }, 2000);

      return () => clearTimeout(timer);
    } else if (previousPosition && previousPosition !== position) {
      // Still track position change, but don't animate
      setPositionChanged(position < previousPosition ? 'up' : 'down');
      const timer = setTimeout(() => setPositionChanged(null), 100);
      return () => clearTimeout(timer);
    }
  }, [position, previousPosition, animated, prefersReducedMotion]);

  // Determine color based on position
  const getColorClass = () => {
    if (position <= 3) return 'gold';
    if (position <= 10) return 'silver';
    return 'gray';
  };

  const colorClass = getColorClass();

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  // Tooltip text based on position
  const getTooltipText = () => {
    if (colorClass === 'gold') return 'Top 3 - Very likely to get a slot!';
    if (colorClass === 'silver') return 'Top 10 - Good chance of getting a slot';
    return 'Keep an eye out for position changes';
  };

  return (
    <div className="relative inline-block">
      <Tooltip content={getTooltipText()} position="top">
        <div
          className={`
            inline-flex items-center gap-1.5 rounded-full font-semibold transition-all duration-300 cursor-help
            ${sizeClasses[size]}
            ${colorClass === 'gold' ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' : ''}
            ${colorClass === 'silver' ? 'bg-gray-100 text-gray-800 border-2 border-gray-400' : ''}
            ${colorClass === 'gray' ? 'bg-gray-100 text-gray-700 border border-gray-300' : ''}
            ${!prefersReducedMotion && showAnimation && positionChanged === 'up' ? 'animate-bounce shadow-lg ring-4 ring-green-300' : ''}
            ${!prefersReducedMotion && showAnimation && positionChanged === 'down' ? 'opacity-75' : ''}
          `}
          role="status"
          aria-label={`Position ${position} in waitlist${positionChanged ? `, moved ${positionChanged}` : ''}`}
        >
        {/* Position number with slide-up animation */}
        <span
          className={`
            font-bold tabular-nums
            ${!prefersReducedMotion && showAnimation && positionChanged === 'up' ? 'animate-slide-up' : ''}
          `}
        >
          #{position}
        </span>

        {/* Directional arrow */}
        {positionChanged && (showAnimation || prefersReducedMotion) && (
          <span
            className={`
              inline-flex
              ${positionChanged === 'up' ? 'text-green-600' : 'text-red-600'}
              ${!prefersReducedMotion && positionChanged === 'up' ? 'animate-bounce' : ''}
            `}
            aria-hidden="true"
          >
            {positionChanged === 'up' ? (
              <TrendingUp className={iconSizes[size]} />
            ) : (
              <TrendingDown className={iconSizes[size]} />
            )}
          </span>
        )}
        </div>
      </Tooltip>

      {/* "You moved up!" micro-notification */}
      {showAnimation && positionChanged === 'up' && !prefersReducedMotion && (
        <div
          className="absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap bg-green-600 text-white text-xs px-2 py-1 rounded-md shadow-lg animate-fade-in-up"
          role="alert"
          aria-live="assertive"
        >
          You moved up!
        </div>
      )}

      {/* CSS for custom animations */}
      <style>{`
        @keyframes slide-up {
          0% {
            transform: translateY(10px);
            opacity: 0;
          }
          50% {
            transform: translateY(-5px);
            opacity: 1;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fade-in-up {
          0% {
            transform: translate(-50%, 10px);
            opacity: 0;
          }
          10% {
            transform: translate(-50%, 0);
            opacity: 1;
          }
          90% {
            transform: translate(-50%, 0);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -5px);
            opacity: 0;
          }
        }

        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 2s ease-out;
        }
      `}</style>
    </div>
  );
}

export default React.memo(WaitlistPositionBadge);
